// src/modules/competition/hooks/useCompetitionStats.js
import { useState, useEffect } from 'react';
import { useService } from '../../../shared/hooks/useService';
import { parseLocalDate } from '../../../shared/utils/dateUtils';

const statsCache = new Map(); // competitionId -> { data, fetchedAt }
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const getCached = (competitionId) => {
    const entry = competitionId ? statsCache.get(competitionId) : null;
    return entry && Date.now() - entry.fetchedAt < CACHE_TTL_MS ? entry.data : null;
};

// Golden angle rotation ensures maximally distinct hues for any number of competitors
const getChartColor = (index) => {
    const hue = Math.round((index * 137.508) % 360);
    return `hsl(${hue}, 65%, 55%)`;
};

const withAlpha = (hslColor, alpha) =>
    hslColor.replace('hsl(', 'hsla(').replace(')', `, ${alpha})`);

// For each competitor, carry-forward the last known cumulative value for every date in sortedDates
const interpolatePoints = (datasets, sortedDates) => {
    const result = {};
    datasets.forEach(dataset => {
        const dateMap = {};
        dataset.data.forEach(p => {
            dateMap[p.x.toISOString().split('T')[0]] = p.y;
        });
        let lastKnown = 0;
        result[dataset.label] = {};
        sortedDates.forEach(date => {
            if (dateMap[date] !== undefined) lastKnown = dateMap[date];
            result[dataset.label][date] = lastKnown;
        });
    });
    return result;
};

export const useCompetitionStats = (competitionId) => {
    // Initialize directly from cache so navigating back shows data instantly.
    const [stats, setStats] = useState(() => getCached(competitionId));
    const [isLoading, setIsLoading] = useState(() => !getCached(competitionId));
    const [error, setError] = useState(null);
    const service = useService();

    useEffect(() => {
        if (!competitionId) {
            setError('No competition ID provided');
            setIsLoading(false);
            return;
        }

        // Cache hit — state already populated by lazy initializer.
        if (getCached(competitionId)) return;

        const fetchStats = async () => {
            try {
                setIsLoading(true);

                const [statsData, competitionData] = await Promise.all([
                    service.getCompetitionStats(competitionId),
                    service.getCompetitionDetails(competitionId)
                ]);

                const {
                    cumulativePointsTimeseries,
                    totalActivities,
                    totalPoints,
                    competition,
                    activityTypeBreakdown
                } = statsData;

                const competitionDetails = competitionData.competition;
                const competitors = competitionDetails?.competitors || [];

                const competitorMap = {};
                competitors.forEach(c => { competitorMap[c.username] = c; });

                // --- Cumulative datasets ---
                const datasets = Object.entries(cumulativePointsTimeseries || {}).map(([username, dataPoints], index) => {
                    const competitor = competitorMap[username] || { firstName: username };
                    const color = getChartColor(index);
                    return {
                        label: competitor.firstName || username,
                        data: dataPoints.map(dp => ({ x: new Date(dp.date), y: dp.points })),
                        borderColor: color,
                        backgroundColor: withAlpha(color, 0.25),
                        fill: false,
                        tension: 0.1
                    };
                });

                // --- Daily datasets ---
                const dailyDatasets = Object.entries(cumulativePointsTimeseries || {}).map(([username, dataPoints], index) => {
                    const competitor = competitorMap[username] || { firstName: username };
                    const color = getChartColor(index);
                    return {
                        label: competitor.firstName || username,
                        data: dataPoints.map((dp, i) => ({
                            x: new Date(dp.date),
                            y: i === 0 ? dp.points : dp.points - dataPoints[i - 1].points
                        })),
                        borderColor: color,
                        backgroundColor: withAlpha(color, 0.25),
                        fill: false,
                        tension: 0.1
                    };
                });

                // --- Days remaining ---
                const endDate = parseLocalDate(competition.endDate);
                const today = new Date();
                const daysRemaining = Math.max(0, Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)));
                const isActive = daysRemaining > 0;

                // --- Projection datasets (active competitions only) ---
                const projectionDatasets = [];
                if (isActive) {
                    const startDate = parseLocalDate(competition.startDate);
                    datasets.forEach(dataset => {
                        if (dataset.data.length === 0) return;
                        const lastPoint = dataset.data[dataset.data.length - 1];
                        const daysActive = Math.max(1, (lastPoint.x - startDate) / (1000 * 60 * 60 * 24));
                        const avgPointsPerDay = lastPoint.y / daysActive;

                        const dayMs = 1000 * 60 * 60 * 24;
                        const projectionData = [{ x: new Date(lastPoint.x), y: lastPoint.y }];
                        let d = new Date(lastPoint.x.getTime() + dayMs);
                        while (d <= endDate) {
                            projectionData.push({
                                x: new Date(d),
                                y: lastPoint.y + avgPointsPerDay * ((d - lastPoint.x) / dayMs)
                            });
                            d = new Date(d.getTime() + dayMs);
                        }

                        if (projectionData.length > 1) {
                            projectionDatasets.push({
                                label: `${dataset.label} (projected)`,
                                data: projectionData,
                                borderColor: dataset.borderColor,
                                backgroundColor: 'transparent',
                                borderDash: [5, 5],
                                borderWidth: 2,
                                pointRadius: 0,
                                fill: false,
                                tension: 0
                            });
                        }
                    });
                }

                // --- Gap datasets (2+ competitors with data) ---
                let gapDatasets = [];
                if (datasets.length > 1) {
                    const allDateSet = new Set();
                    datasets.forEach(d => d.data.forEach(p => allDateSet.add(p.x.toISOString().split('T')[0])));
                    const sortedDates = Array.from(allDateSet).sort();

                    if (sortedDates.length > 0) {
                        const interpolated = interpolatePoints(datasets, sortedDates);
                        gapDatasets = datasets
                            .map(dataset => ({
                                label: dataset.label,
                                data: sortedDates.map(date => {
                                    const leaderPoints = Math.max(...datasets.map(d => interpolated[d.label][date] || 0));
                                    return { x: new Date(date), y: leaderPoints - (interpolated[dataset.label][date] || 0) };
                                }),
                                borderColor: dataset.borderColor,
                                backgroundColor: dataset.backgroundColor,
                                fill: false,
                                tension: 0.1,
                                pointRadius: 2
                            }))
                            .filter(d => d.data.some(p => p.y > 0)); // exclude the leader (all zeros)
                    }
                }

                // --- Winner and final margin (concluded competitions) ---
                let winner = null;
                let finalMargin = null;
                if (!isActive && datasets.length > 0) {
                    const finalStandings = datasets
                        .map(d => ({ label: d.label, points: d.data.length > 0 ? d.data[d.data.length - 1].y : 0 }))
                        .sort((a, b) => b.points - a.points);
                    winner = finalStandings[0]?.label || null;
                    if (finalStandings.length >= 2) {
                        finalMargin = parseFloat((finalStandings[0].points - finalStandings[1].points).toFixed(1));
                    }
                }

                // --- Activity type breakdown keyed by first name ---
                const activityBreakdownByName = {};
                Object.entries(activityTypeBreakdown || {}).forEach(([username, breakdown]) => {
                    const competitor = competitorMap[username] || { firstName: username };
                    const name = competitor.firstName || username;
                    activityBreakdownByName[name] = breakdown;
                });

                const result = {
                    datasets,
                    dailyDatasets,
                    projectionDatasets,
                    gapDatasets,
                    totalActivities: totalActivities || 0,
                    totalPoints: parseFloat(totalPoints) || 0,
                    winner,
                    finalMargin,
                    activityTypeBreakdown: activityBreakdownByName,
                    competition: {
                        startDate: competition.startDate,
                        endDate: competition.endDate,
                        competitors,
                        daysRemaining,
                        winnerCalculationMethod: competition.winnerCalculationMethod,
                    }
                };
                statsCache.set(competitionId, { data: result, fetchedAt: Date.now() });
                setStats(result);
                setError(null);
            } catch (err) {
                console.error('Error fetching competition stats:', err);
                setError(err.message || 'Failed to load statistics');
            } finally {
                setIsLoading(false);
            }
        };

        fetchStats();
    }, [service, competitionId]);

    return { stats, isLoading, error };
};
